import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, trackPageview } from "../lib/api";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { IslandEssentials } from "../components/IslandEssentials";
import { openSubscribe } from "../components/SubscribeModalHost";
import { LangContext } from "../lib/LangContext";
import { useSeoTags, useJsonLd } from "../lib/useSeoTags";
import { t as translate } from "../lib/i18n";
import { SEO_PAGES_BY_SLUG, getHreflangAlternates } from "../lib/seoLandingPages";

// Renders a single static SEO landing page. `slug` selects the entry
// from SEO_PAGES_BY_SLUG. Header + Footer are shared with the rest of
// the site so the landing page fits visually with the main product.
export default function SeoLandingPage({ slug }) {
  const params = useParams();
  const effectiveSlug = slug || params.slug;
  const page = SEO_PAGES_BY_SLUG[effectiveSlug];

  useEffect(() => {
    if (effectiveSlug) trackPageview(`/${effectiveSlug}`);
  }, [effectiveSlug]);

  // Meta tags — locked to the page's language (LangContext still exists so
  // Header/Footer render in the same locale as the page copy).
  useSeoTags(
    page?.lang || "en",
    page
      ? {
          [page.lang]: {
            title: page.title,
            description: page.description,
            canonical: page.canonical,
            alternates: getHreflangAlternates(page.slug),
          },
        }
      : {}
  );

  // FAQ JSON-LD — only for English variants per spec.
  const faqJsonLd = useMemo(() => {
    if (!page?.faq || page.lang !== "en") return null;
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: page.faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };
  }, [page]);
  useJsonLd(`seo-faq-${effectiveSlug}`, faqJsonLd);

  if (!page) {
    // 404 rendered before we know a page.lang — fall back to browser's
    // stored language (or English). Not wrapped in LangContext so use
    // `translate()` directly.
    const fallbackLang = (() => {
      try {
        const s = localStorage.getItem("ir_lang");
        if (s === "en" || s === "nl" || s === "es") return s;
      } catch (e) { /* ignore */ }
      return "en";
    })();
    return (
      <div className="min-h-screen bg-white p-8" data-testid="seo-page-404">
        <p>{translate(fallbackLang, "error_page_not_found")}</p>
        <Link to="/" className="text-[#0AADA0]">← Home</Link>
      </div>
    );
  }

  return (
    <LangContext.Provider value={page.lang}>
      <div className="min-h-screen bg-white text-[#1A1A1A]" data-testid={`seo-page-${effectiveSlug}`}>
        <Header
          category="All"
          onCategoryChange={() => {}}
          island="all"
          onIslandChange={() => {}}
          lang={page.lang}
          onLangChange={() => {}}
          onSubscribe={() => openSubscribe("seo_landing")}
        />

        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <article>
            <h1
              className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-tight text-[#1A2E35] mb-4"
              data-testid="seo-page-h1"
            >
              {page.h1}
            </h1>
            <p className="text-[16px] text-[#374151] leading-relaxed mb-8" data-testid="seo-page-intro">
              {page.intro}
            </p>

            {/* Live data embed */}
            <div className="mb-10" data-testid={`seo-embed-${page.embedType}`}>
              <LiveEmbed embedType={page.embedType} embedProps={page.embedProps} lang={page.lang} />
            </div>

            {/* Long-form sections */}
            {page.sections.map((s, i) => (
              <section key={i} className="mb-8" data-testid={`seo-section-${i}`}>
                <h2 className="font-serif-display text-2xl text-[#1A2E35] mb-3">{s.h2}</h2>
                <p className="text-[15px] text-[#374151] leading-relaxed">{s.body}</p>
              </section>
            ))}

            {/* FAQ */}
            {page.faq && page.faq.length > 0 && (
              <section className="mb-10 mt-12" data-testid="seo-faq">
                <h2 className="font-serif-display text-2xl text-[#1A2E35] mb-4">FAQ</h2>
                <div className="space-y-5">
                  {page.faq.map((f, i) => (
                    <div key={i} data-testid={`seo-faq-item-${i}`}>
                      <h3 className="text-[15px] font-semibold text-[#1A2E35] mb-1.5">{f.q}</h3>
                      <p className="text-[14px] text-[#374151] leading-relaxed">{f.a}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Related links */}
            {page.relatedLinks && page.relatedLinks.length > 0 && (
              <section className="mt-12 pt-8 border-t border-[#EEEEEE]" data-testid="seo-related">
                <h2 className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-3">
                  Related
                </h2>
                <ul className="space-y-2">
                  {page.relatedLinks.map((l, i) => (
                    <li key={i}>
                      <Link
                        to={l.href}
                        className="text-[14px] text-[#0AADA0] hover:underline"
                        data-testid={`seo-related-link-${i}`}
                      >
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </article>
        </main>

        <Footer onSubscribe={() => openSubscribe("seo_landing_footer")} />
      </div>
    </LangContext.Provider>
  );
}

// ---------- Live data embed dispatcher ----------
const LiveEmbed = ({ embedType, embedProps, lang }) => {
  if (embedType === "latest-news") {
    return <LatestNewsEmbed island={embedProps.island} limit={embedProps.limit} lang={lang} />;
  }
  if (embedType === "daily-top-stories") {
    return <DailyTopStoriesEmbed island={embedProps.island} lang={lang} />;
  }
  if (embedType === "cruise-today" || embedType === "pharmacy-duty") {
    // Both reuse the shared IslandEssentials strip — it already renders
    // the pharmacy and cruise cards driven off /api/essentials and hides
    // the ones with no data. Simplest way to keep one source of truth.
    return <IslandEssentials island={embedProps.island} />;
  }
  return null;
};

const DailyTopStoriesEmbed = ({ island, lang }) => {
  // Phase 2 canonical per-island top-5 — sourced from daily_top_stories.
  // If the cron hasn't populated today yet, the backend serves the most
  // recent day it does have. If nothing exists (brand-new island), falls
  // back to LatestNewsEmbed so the briefing page is never empty.
  const [items, setItems] = useState(null);
  useEffect(() => {
    let cancelled = false;
    // Cross-island briefing pages pass island="all" — resolve to the
    // full slate by making 4 parallel calls and interleaving. Simpler
    // approach for now: hit the "aruba" collection since that's the
    // primary market. For per-island briefing this is a straight call.
    if (island && island !== "all") {
      api.get(`/top-stories/${island}`, { params: { lang } })
        .then((r) => { if (!cancelled) setItems(r.data?.stories || []); })
        .catch(() => { if (!cancelled) setItems([]); });
    } else {
      // Cross-island: merge top stories from all 4 islands.
      Promise.all(
        ["aruba", "curacao", "bonaire", "stmaarten"].map((isl) =>
          api.get(`/top-stories/${isl}`, { params: { lang } })
            .then((r) => (r.data?.stories || []).map((s) => ({ ...s, _island: isl })))
            .catch(() => [])
        )
      ).then((results) => {
        if (cancelled) return;
        // Interleave: pick top 1 from each island in rotation, take up to 6.
        const merged = [];
        const maxLen = Math.max(...results.map((r) => r.length), 0);
        for (let i = 0; i < maxLen && merged.length < 6; i++) {
          for (const r of results) {
            if (r[i]) merged.push(r[i]);
            if (merged.length >= 6) break;
          }
        }
        setItems(merged);
      });
    }
    return () => { cancelled = true; };
  }, [island, lang]);

  if (items === null) {
    return (
      <div className="py-6 text-center text-[13px] text-[#6B7280]" data-testid="seo-top-loading">
        Loading today&apos;s top stories…
      </div>
    );
  }
  if (!items.length) {
    // Fallback to latest news if daily_top_stories is empty for this island
    return <LatestNewsEmbed island={island} limit={6} lang={lang} />;
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="seo-top-grid">
      {items.map((s) => (
        <Link
          key={s.article_id}
          to={s.story_url}
          className="block p-4 border border-[#EEEEEE] rounded-lg hover:border-[#0AADA0] transition-colors"
          data-testid={`seo-top-item-${s.article_id}`}
        >
          <div className="text-[10px] uppercase tracking-wider text-[#0AADA0] mb-1">
            {s.category}
          </div>
          <h3 className="text-[14px] font-medium text-[#1A2E35] leading-snug mb-1">
            {s.title}
          </h3>
          <p className="text-[12px] text-[#6B7280] line-clamp-2">
            {s.summary}
          </p>
          <div className="mt-2 text-[11px] text-[#6B7C82]">
            {s.source_name}
          </div>
        </Link>
      ))}
    </div>
  );
};

const LatestNewsEmbed = ({ island, limit = 6, lang }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const params = { lang, limit };
    if (island && island !== "all") params.island = island;
    api
      .get("/articles", { params })
      .then((r) => { if (!cancelled) { setItems((r.data || []).slice(0, limit)); setLoading(false); } })
      .catch(() => { if (!cancelled) { setItems([]); setLoading(false); } });
    return () => { cancelled = true; };
  }, [island, limit, lang]);

  if (loading) {
    return (
      <div className="py-6 text-center text-[13px] text-[#6B7280]" data-testid="seo-news-loading">
        Loading latest stories…
      </div>
    );
  }
  if (!items.length) {
    return (
      <div className="py-6 text-center text-[13px] text-[#6B7280]" data-testid="seo-news-empty">
        No stories available yet.
      </div>
    );
  }
  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 gap-4"
      data-testid="seo-news-grid"
    >
      {items.map((a) => (
        <Link
          key={a.id}
          to={`/article/${a.id}${a.slug ? `-${a.slug}` : ""}`}
          className="block p-4 border border-[#EEEEEE] rounded-lg hover:border-[#0AADA0] transition-colors"
          data-testid={`seo-news-item-${a.id}`}
        >
          <div className="text-[10px] uppercase tracking-wider text-[#0AADA0] mb-1">
            {a.category}
          </div>
          <h3 className="text-[14px] font-medium text-[#1A2E35] leading-snug mb-1">
            {a.title}
          </h3>
          <p className="text-[12px] text-[#6B7280] line-clamp-2">
            {a.ai_summary}
          </p>
          <div className="mt-2 text-[11px] text-[#6B7C82]">
            {a.source_name}
          </div>
        </Link>
      ))}
    </div>
  );
};
